import { formatUrl } from 'url-lib';
import Cookies from 'cookies-js';

import { fetchJSON } from '@eb/http';
import { pruneObject } from '@eb/destination-utils';
import omit from 'lodash/omit';
import moment from 'moment-timezone';

import { deepKeysToSnake, keysCamelToSnake } from '@eb/transformation-utils';
import { CURRENT_FUTURE, QUALITY } from '../constants';

const searchUrl = '/api/v3/destination/search/';
const searchAutocompleteSuggestionsUrl =
    '/api/v3/destination/search/autocomplete/';

const checkIfOverridingDedupExperiment = (experimentOverride) =>
    experimentOverride
        ? experimentOverride.experimentName === 'search_exp_1' &&
          experimentOverride.variant === 'D'
        : false;

const shouldShowDebugInfo = (isStaff, history, experimentOverride) => {
    const isOverridingDedupExperiment = checkIfOverridingDedupExperiment(
        experimentOverride,
    );

    // Backend does not support debug info when deduping is used
    // https://github.com/eventbrite/search_service//blob/f272067cfc357b14742262b135379cc035985a8c/search_service/common/elasticsearch/builders/execute.py#L168
    if (isOverridingDedupExperiment) {
        return false;
    }

    if (!history) {
        return false;
    }

    return isStaff && history.location.hash === '#debug';
};

const shouldOverrideExperiment = (
    history,
    isStaff,
    experimentOverride,
    searchQueryFiltered,
) => {
    //Don't override if history hasn't been defined yet
    if (!history) {
        return false;
    }

    // Don't override unless the debug tool is being used
    if (history.location.hash !== '#debug') {
        return false;
    }

    // If in the dedup experiment, the backend will dedup. But since we're expanding on the dupes cluster,
    // we want to actually show them un-deduped.
    if (searchQueryFiltered.hash) {
        return false;
    }

    return isStaff && experimentOverride.experimentName !== 'none';
};

// Check if the client_timezone of the query is correct. If we
// received it from the server, it may not be correct because
// it was guessed with GeoIP rather than browser-side JS.
// There may not be any client_timezone at all, this is currently only
// sent for online searches.
const CLIENT_TIMEZONE_COOKIE_NAME = 'client_timezone';
const repairClientTimezone = (searchQuery) => {
    if (searchQuery.clientTimezone) {
        const momentGuessedTimezone = moment.tz.guess();

        if (
            momentGuessedTimezone &&
            searchQuery.clientTimezone !== momentGuessedTimezone
        ) {
            Cookies.expire(CLIENT_TIMEZONE_COOKIE_NAME);
            Cookies.set(CLIENT_TIMEZONE_COOKIE_NAME, momentGuessedTimezone);

            return {
                ...searchQuery,
                clientTimezone: momentGuessedTimezone,
            };
        }
    }

    return searchQuery;
};

// The search backend will attempt to detect duplicate events (events that
// should have been created as repeating events but organizer chose not to).
// On the frontend we want to ask the backend to dedup, but we also give the
// user the option to "expand" the duplicated events so in some cases we don't want
// to dedup.
const shouldRequestDedupedEvents = (
    experimentOverride,
    searchQueryFiltered,
) => {
    const isOverridingDedupExperiment = checkIfOverridingDedupExperiment(
        experimentOverride,
    );

    if (!isOverridingDedupExperiment) {
        return false;
    }

    // If hash is part of the searchQuery then we never want to dedup,
    // since it means the user has "expanded" on a cluster of deduped events.
    return !searchQueryFiltered.hash;
};

export const search = ({
    searchQuery,
    experimentOverride,
    isStaff,
    history,
}) => {
    // The search API allows either a bbox or a WOF placeID but not both
    let searchQueryFiltered =
        searchQuery.bbox && searchQuery.places
            ? omit(searchQuery, 'places')
            : searchQuery;

    searchQueryFiltered = repairClientTimezone(searchQueryFiltered);

    const shouldDedup = shouldRequestDedupedEvents(
        experimentOverride,
        searchQueryFiltered,
    );

    let requestParameters = {
        event_search: deepKeysToSnake(
            pruneObject({
                ...searchQueryFiltered,
                dedup: shouldDedup,
                page_size: 20,
            }),
        ),
        'expand.destination_event': [
            'primary_venue',
            'image',
            'ticket_availability',
            'saves',
            'my_collections',
            'event_sales_status',
        ],
    };

    if (shouldShowDebugInfo(isStaff, history, experimentOverride)) {
        requestParameters = {
            ...requestParameters,
            debug: true,
        };
    }

    if (
        shouldOverrideExperiment(
            history,
            isStaff,
            experimentOverride,
            searchQueryFiltered,
        )
    ) {
        requestParameters = {
            ...requestParameters,
            debug_experiment_overrides: {
                [experimentOverride.experimentName]: experimentOverride.variant,
            },
        };
    }

    const fetchReq = fetchJSON(searchUrl, {
        method: 'POST',
        body: JSON.stringify(requestParameters),
    });

    return fetchReq;
};

export const searchAutocompleteSuggestions = ({
    placeId,
    onlineEventsOnly,
    query,
}) => {
    let queryParams = {
        placeId,
        q: query,
        completionTypes: ['event', 'query'],
        'expand.destination_event': ['primary_venue', 'image'],
    };

    if (onlineEventsOnly) {
        queryParams.onlineEventsOnly = true;
    }

    queryParams = keysCamelToSnake(queryParams);
    return fetchJSON(formatUrl(searchAutocompleteSuggestionsUrl, queryParams));
};

export const searchSuggestionsForCity = ({ placeId, onlineEventsOnly }) =>
    fetchJSON(searchUrl, {
        method: 'POST',
        body: JSON.stringify({
            event_search: {
                places: placeId ? [placeId] : [],
                online_events_only: onlineEventsOnly,
                dates: CURRENT_FUTURE,
                sort: QUALITY,
                aggs: {
                    organizertagsautocomplete_agg: { size: 50 },
                    tags: {},
                    dates: {},
                },
            },
            'expand.destination_event': [
                'primary_venue',
                'image',
                'ticket_availability',
                'saves',
                'my_collections',
            ],
        }),
    });
